import React from "react"

import { Badge, Flex, Progress, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../Components/Button"
import DeadlinePill from "../../../../../../Components/DeadlinePill"
import InfoTooltip from "../../../../../../Components/InfoTooltip/InfoTooltip"
import { RoutesPaths } from "../../../../../../constants/user-routes"
import Icon from "../../../../../../Icon"
import Images from "../../../../../../Images"
import { formatCurrency, paymentProcessorMap } from "../../../../../../Utils/utils"
import { TYPE_LABELS } from "../../constants/statusMeta"

import "./DisputeCard.less"

const { Text } = Typography

// Grid-view card for a dispute, built to the same shape as the Alerts AlertCard so
// the two lists read as one product: grey identity chips + icon actions in the head,
// a 2-column field list with copyable identifiers, then the status row sitting
// directly above the actions.
const DisputeCard = ({
  dispute,
  statusColors,
  statusLabels,
  labelForCategory,
  renderAssignee,
  onView,
  onPin
}) => {
  const { t } = useTranslation()
  const d = dispute
  // Same three statuses that are actionable everywhere else (detail action bar,
  // sidebar badge, Home action list): a reopened cycle owes a response again, so
  // its deadline matters here too.
  const actionable = ["needs_response", "awaiting_review"].includes(d.status)
  // The deadline is shown as the date itself plus the time remaining, so the card
  // answers "when" and not only "how long". Same 'DD MMM YYYY' format as every other
  // date in the product.
  const dueDate = d.due_by ? dayjs(d.due_by).format("DD MMM YYYY") : null
  // The time remaining is the table's own DeadlinePill tag, not a hand-rolled string, so
  // the wording, thresholds and colour can never drift between the two views.
  const enrichmentPct =
    d.enrichment == null ? null : Math.max(0, Math.min(100, Math.round(d.enrichment)))
  // Same battery-gauge thresholds and semantic colours as the table's Enrichment column:
  // red below 34, orange 34-66, green from 67.
  const enrichmentStroke =
    enrichmentPct == null
      ? undefined
      : enrichmentPct >= 67
        ? "#16895A"
        : enrichmentPct >= 34
          ? "#DF7009"
          : "#D71313"
  // Processor chip, same source as the dispute detail topbar.
  const processorLabel = d.processor ? paymentProcessorMap[d.processor] || d.processor : null
  const processorIcon = d.processor ? Images[`integration_${d.processor}`] : null

  // Six fields, paired so each row groups like with like. The deadline lives beside the
  // status and Assignee is its own full-width row, which is what leaves this an even
  // 3x2 grid with no ragged trailing cell.
  const fields = [
    { label: t("Amount"), value: formatCurrency(Number(d.amount) || 0) },
    { label: t("Customer"), value: d.customer_full_name || "." },
    { label: t("Category"), value: d.reason_category_label || labelForCategory(d.reason_category) },
    {
      label: t("Reason code"),
      value: d.reason_code || ".",
      tooltip: t(
        "The card network's specific code for why this payment was disputed (e.g. Visa 10.4 is fraud in a card-absent sale). It decides which evidence is most likely to win the dispute."
      )
    },
    {
      label: t("Dispute date"),
      value: d.dispute_created_at ? dayjs(d.dispute_created_at).format("DD MMM YYYY") : "."
    },
    {
      label: t("Enrichment"),
      // Shown for EVERY status, resolved ones included: the score stays useful after the
      // fact when reviewing what was submitted. Rendered as the table's gauge + percent,
      // reusing its classes so the bar is identical in both views.
      value: enrichmentPct == null ? "." : `${enrichmentPct}%`,
      node:
        enrichmentPct == null ? null : (
          <span className="dispute-enrichment-cell">
            <Progress
              percent={enrichmentPct}
              showInfo={false}
              strokeColor={enrichmentStroke}
              className="dispute-enrich-bar"
            />
            <Text className="dispute-enrichment-pct">{enrichmentPct}%</Text>
          </span>
        )
    }
  ]

  return (
    <div className="dispute-card" onClick={() => onView(d)}>
      <Flex align="center" justify="space-between" gap={10} className="dispute-card_head">
        <Flex gap={8} style={{ minWidth: 0 }}>
          {/* The dispute ID is what people quote to each other, so it leads here instead
              of the internal sequence number. Middle-ellipsis (suffix keeps the last four
              characters visible): ids share a long common prefix, so an END-truncated id
              rendered every card as the same "du_DEM..." and they could not be told apart. */}
          <div className="grey-block">
            <Text
              strong
              ellipsis={{
                tooltip: d.processor_dispute_id,
                suffix: (d.processor_dispute_id || "").slice(-4)
              }}
            >
              {(d.processor_dispute_id || "").slice(0, -4)}
            </Text>
          </div>
          {processorLabel ? (
            /* Icon-only on the card: the logo is instantly recognizable and the name is one
               hover away, which frees the head's width for the full dispute id and the Type
               chip (three text chips did not fit and everything truncated at once). The
               table keeps the text label. Processors without a logo keep their name. */
            <Tooltip title={processorLabel}>
              <div className="grey-block">
                {processorIcon ? (
                  <img width={16} height={16} src={processorIcon} alt={processorLabel} />
                ) : (
                  <Text strong>{processorLabel}</Text>
                )}
              </div>
            </Tooltip>
          ) : null}
          {/* Case type (Dispute vs Inquiry) as an identity chip, same grey-block shape as
              the id and processor so the head keeps reading as one row of labels. */}
          <div className="grey-block">
            <Text strong>{t(TYPE_LABELS[d.dispute_type] || TYPE_LABELS.dispute)}</Text>
          </div>
        </Flex>
        <Flex gap={4} align="center">
          <CopyLinkBtn dispute={d} t={t} />
          <Tooltip title={d.pinned ? t("Unpin") : t("Pin to top")}>
            <Button
              type="text"
              shape="circle"
              className="dispute-card_pin"
              aria-label={d.pinned ? t("Unpin") : t("Pin to top")}
              icon={<Icon type={d.pinned ? "pin_active" : "pin"} />}
              onClick={(e) => {
                e.stopPropagation()
                onPin(d)
              }}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <div className="dispute-card_fields">
        {fields.map((f) => (
          <div key={f.label} className="dispute-card_field">
            <span className="dispute-card_field_label">
              <Text type="secondary" strong ellipsis>
                {f.label}
              </Text>
              {f.tooltip ? (
                <InfoTooltip className="dispute-card_field_tip" title={f.tooltip} />
              ) : null}
            </span>
            {/* The copy button is a sibling of the text, not a child of it: inside an
                ellipsised Text the button is part of the clipped content, so a long
                enough value hides the copy affordance on some rows and not others. */}
            {f.node ? (
              /* A field whose value is rendered markup (the enrichment gauge) rather than
                 text. Kept in the same cell shape as the others so the grid is unaffected. */
              f.node
            ) : f.copyable ? (
              <span className="dispute-card_field_value dispute-card_field_value--copy">
                <Text strong ellipsis={{ tooltip: f.value }}>
                  {f.value}
                </Text>
                <Text copyable={{ icon: <Icon type="copy" />, text: f.value }} />
              </span>
            ) : (
              <Text className="dispute-card_field_value" strong ellipsis={{ tooltip: f.value }}>
                {f.value}
              </Text>
            )}
          </div>
        ))}
      </div>

      {/* Status sits directly above the action, like the Alerts card's status control,
          rather than in the head. Single action: the transaction deep-link was dropped,
          so View details takes the full width rather than sharing the row. */}
      <Flex vertical gap={12}>
        {/* Assignee, the status pill and the action are one stack of three full-width rows
            of the same shape and height, so the bottom of the card reads as a set rather
            than three unrelated pieces. Label, divider, value, exactly like the status. */}
        {renderAssignee ? (
          <Flex gap={8} justify="center" align="center" className="dispute-card_assignee">
            <Text type="secondary" strong>
              {t("Assignee")}
            </Text>
            <div className="dispute-card_status_divider" />
            {renderAssignee(d)}
          </Flex>
        ) : null}
        <Flex gap={8} align="center" className="dispute-card_status">
          {/* Same shape as the Alerts card's status control: one bordered, full-width
              row. For anything still actionable the deadline follows after a divider, as
              the due date plus the time left, so "when" and "how urgent" sit together.
              The cycle stage is deliberately not shown: it was noise on a card this dense. */}
          <Flex gap={8} justify="center" align="center" className="dispute-card_status_tag">
            {(statusColors[d.status] || "default") === "default" ? (
              <Badge status="default" />
            ) : (
              <Badge color={statusColors[d.status]} />
            )}
            <Text strong>{t(statusLabels[d.status] || d.status)}</Text>
            {actionable && d.due_by ? (
              <>
                <div className="dispute-card_status_divider" />
                {/* When the row runs out of width, the date is the piece that gives way
                    (ellipsis): the status name and the countdown pill carry the urgency
                    and must never wrap or clip (see the LESS). */}
                {dueDate ? (
                  <Text strong className="dispute-card_status_date">
                    {dueDate}
                  </Text>
                ) : null}
                {/* The table's own countdown tag, so wording and colour match exactly. */}
                <DeadlinePill due_by={d.due_by} />
              </>
            ) : null}
          </Flex>
        </Flex>
        <Button
          block
          type="default"
          text={t("View details")}
          onClick={(e) => {
            e.stopPropagation()
            onView(d)
          }}
        />
      </Flex>
    </div>
  )
}

// Copies a deep link to this dispute, mirroring the Alerts card's link button.
const CopyLinkBtn = ({ dispute, t }) => {
  const [copied, setCopied] = React.useState(false)
  const copy = (e) => {
    e.stopPropagation()
    try {
      navigator.clipboard.writeText(
        `${window.location.origin}${RoutesPaths.recover.details(dispute.id)}`
      )
    } catch (err) {
      /* clipboard unavailable (insecure context / permissions) */
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <Tooltip title={copied ? t("Copied!") : t("Copy link")}>
      <Button
        type="text"
        shape="circle"
        className="dispute-card_pin"
        aria-label={t("Copy link")}
        icon={<Icon type="link" />}
        onClick={copy}
      />
    </Tooltip>
  )
}

export default DisputeCard
